import convert from 'color-convert';

/**
 * Derives the CTA hover/active shades from the API-provided brand
 * `background_color`.
 *
 * The brand colour reaches the button as the `--bg-color` CSS variable (set by
 * ApplyFlowShell). This converts that hex to HSL via `color-convert` and nudges
 * the lightness to build darker hover / active shades, exposed as sibling CSS
 * variables the `brand` Button variant consumes. Non-hex input (the `'inherit'`
 * fallback, or a junk query-param) yields `{}`, so the button falls back to its
 * base colour.
 */

const HEX6 = /^#?([0-9a-fA-F]{6})$/;
const HEX3 = /^#?([0-9a-fA-F]{3})$/;

/** `#abc` / `abc` / `#aabbcc` → 6-digit hex without `#`, or `null` if not hex. */
function normalizeHex(input?: string): string | null {
  if (!input) return null;
  const s = input.trim();
  const m6 = s.match(HEX6);
  if (m6) return m6[1].toLowerCase();
  const m3 = s.match(HEX3);
  if (m3)
    return m3[1]
      .split('')
      .map((c) => c + c)
      .join('')
      .toLowerCase();
  return null;
}

const clampL = (l: number) => Math.max(0, Math.min(100, l));
const hsl = (h: number, s: number, l: number, alpha?: number) =>
  alpha == null
    ? `hsl(${h} ${s}% ${clampL(l)}%)`
    : `hsl(${h} ${s}% ${clampL(l)}% / ${alpha})`;

/** Lightness deltas for the derived states (percentage points). */
const HOVER_DELTA = 8;
const ACTIVE_DELTA = 15;
/** At/below this lightness the brand is near-black, so we lighten instead of
 *  darken — otherwise hover/active would clamp to black and show no change. */
const DARK_L = 12;

export type BrandStateVars = {
  '--bg-color-hover': string;
  '--bg-color-active': string;
  '--bg-color-shade': string;
};

/**
 * CSS custom properties for the brand CTA's hover/active shades, or `{}` when
 * `bg` is not a hex colour. Spread into an element's `style`. Typed as
 * `Partial` (not a union with an empty object) so the spread stays a
 * string-valued literal that casts cleanly to `CSSProperties`.
 */
export function brandStateVars(bg?: string): Partial<BrandStateVars> {
  const clean = normalizeHex(bg);
  if (!clean) return {};

  const [h, s, l] = convert.hex.hsl(clean);
  const dir = l <= DARK_L ? 1 : -1;

  return {
    '--bg-color-hover': hsl(h, s, l + dir * HOVER_DELTA),
    '--bg-color-active': hsl(h, s, l + dir * ACTIVE_DELTA),
    '--bg-color-shade': hsl(h, s, l + dir * ACTIVE_DELTA, 0.1),
  };
}

/**
 * Exact hex for the app's `--primary` (222 69% 30%). Hardcoded — not
 * `hsl(var(--primary))` — because `.dark`'s `--primary` is near-white and the
 * shade math (`brandStateVars`) / `NoDataState.isHex` need a concrete hex.
 * Keep in sync with `--primary` in `src/containers/App/App.css`.
 */
export const PRIMARY_HEX = '#183781';

/** HSL lightness at/above which a colour is too light to read as an accent. */
const WHITE_L = 85;

/** True for the `white` keyword or any hex whose lightness ≥ WHITE_L. */
export function isNearWhite(color?: string): boolean {
  if (!color) return false;
  if (color.trim().toLowerCase() === 'white') return true;
  const clean = normalizeHex(color);
  return clean != null && convert.hex.hsl(clean)[2] >= WHITE_L;
}

/**
 * Colour for brand ACCENTS (StepProgress fill, CTA, NoDataState tint) — the
 * brand `background_color`, or the app primary when that background is
 * white/near-white so the accent stays visible on the white sheet. The hero
 * background is intentionally NOT run through this (it keeps the real colour).
 */
export function actionColor(bg: string): string {
  return isNearWhite(bg) ? PRIMARY_HEX : bg;
}

/** Near-black ink used for the CTA label on light backgrounds. */
const DARK_INK = '#16171a';

/**
 * Legible label colour for a filled button of the given background — dark ink
 * on light backgrounds, white on dark — using the standard YIQ brightness
 * heuristic (threshold 128). Lets the CTA text depend solely on its own
 * background instead of the API `text_color`. Falls back to dark ink for
 * non-hex input.
 */
export function contrastText(bg: string): string {
  const clean = normalizeHex(bg);
  if (!clean) return DARK_INK;
  const [r, g, b] = convert.hex.rgb(clean);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 128 ? DARK_INK : '#ffffff';
}
